/**
 * Debounce do indicador visual de qualidade de conexão (UI).
 * Complementa useSubscriberNetworkAdaptation (mitigação de mídia, grace 5s screenshare).
 */

export type DisplayConnectionQuality = 'excellent' | 'good' | 'poor' | 'lost' | 'unknown';

export const CONNECTION_QUALITY_UI = {
  /** Não exibir poor/lost nos primeiros N ms após iniciar screenshare (participante local). */
  SCREENSHARE_GRACE_MS: 8_000,
  /** Poor/lost precisa persistir este tempo antes de aparecer no ícone. */
  POOR_DISPLAY_SUSTAINED_MS: 3_000,
} as const;

export const CONNECTION_QUALITY_TRANSIENT_HINT =
  'A conexão pode parecer instável por alguns segundos após compartilhar a tela. Isso é normal.';

export function isNegativeConnectionQuality(quality: string): boolean {
  return quality === 'poor' || quality === 'lost';
}

export function normalizeStableDisplayQuality(quality: string): DisplayConnectionQuality {
  if (
    quality === 'excellent' ||
    quality === 'good' ||
    quality === 'poor' ||
    quality === 'lost'
  ) {
    return quality;
  }
  return 'unknown';
}

export type ConnectionQualityDisplayState = {
  poorSince: number | null;
  lastStableDisplay: DisplayConnectionQuality;
  screenShareStartedAt: number | null;
};

export type ResolveConnectionQualityDisplayInput = {
  rawQuality: string;
  now: number;
  participantIsLocal: boolean;
  state: ConnectionQualityDisplayState;
};

export type ResolveConnectionQualityDisplayResult = ConnectionQualityDisplayState & {
  displayQuality: DisplayConnectionQuality;
  isDebouncingPoor: boolean;
  showTransientHint: boolean;
};

/**
 * Calcula a qualidade exibida no ícone a partir da qualidade bruta do LiveKit.
 */
export function resolveConnectionQualityDisplay(
  input: ResolveConnectionQualityDisplayInput,
): ResolveConnectionQualityDisplayResult {
  const { rawQuality, now, participantIsLocal, state } = input;
  const raw = normalizeStableDisplayQuality(rawQuality);

  const inScreenShareGrace =
    participantIsLocal &&
    state.screenShareStartedAt !== null &&
    now - state.screenShareStartedAt < CONNECTION_QUALITY_UI.SCREENSHARE_GRACE_MS;

  if (inScreenShareGrace && isNegativeConnectionQuality(raw)) {
    const fallback =
      state.lastStableDisplay === 'unknown' || isNegativeConnectionQuality(state.lastStableDisplay)
        ? 'good'
        : state.lastStableDisplay;
    return {
      displayQuality: fallback,
      poorSince: null,
      lastStableDisplay: fallback,
      screenShareStartedAt: state.screenShareStartedAt,
      isDebouncingPoor: true,
      showTransientHint: true,
    };
  }

  if (!isNegativeConnectionQuality(raw)) {
    const lastStable =
      raw !== 'unknown' && !isNegativeConnectionQuality(raw)
        ? raw
        : state.lastStableDisplay === 'unknown' || isNegativeConnectionQuality(state.lastStableDisplay)
          ? 'good'
          : state.lastStableDisplay;
    const displayQuality: DisplayConnectionQuality =
      raw === 'unknown' ? lastStable : raw;
    return {
      displayQuality,
      poorSince: null,
      lastStableDisplay: lastStable,
      screenShareStartedAt: state.screenShareStartedAt,
      isDebouncingPoor: false,
      showTransientHint: false,
    };
  }

  const poorSince = state.poorSince ?? now;
  const sustainedMs = now - poorSince;

  if (sustainedMs < CONNECTION_QUALITY_UI.POOR_DISPLAY_SUSTAINED_MS) {
    const fallback =
      state.lastStableDisplay === 'unknown' || isNegativeConnectionQuality(state.lastStableDisplay)
        ? 'good'
        : state.lastStableDisplay;
    return {
      displayQuality: fallback,
      poorSince,
      lastStableDisplay: state.lastStableDisplay,
      screenShareStartedAt: state.screenShareStartedAt,
      isDebouncingPoor: true,
      showTransientHint: inScreenShareGrace,
    };
  }

  return {
    displayQuality: raw,
    poorSince,
    lastStableDisplay: raw,
    screenShareStartedAt: state.screenShareStartedAt,
    isDebouncingPoor: false,
    showTransientHint: false,
  };
}
